/**
 * ScheduleComparisonTable
 * Shared current-vs-proposed schedule comparison table used by every
 * schedule-impact preview (rate change, capital amendment, interest payment,
 * extension, rollover, regeneration). One table = one presentation, so the same
 * numbers render the same way everywhere (financial-system consistency).
 *
 * Rows are pre-built by the caller (RateChangePreviewModal keeps its split-aware
 * builder; amendment/regen previews use buildComparisonRows). Each row carries
 * optional `current_calc_detail` / `calc_detail` objects that drive the Σ
 * calculation-detail popover. The component owns the popover state internally.
 */

import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Typography,
  IconButton,
  Popover,
  Divider,
  Tooltip,
} from '@mui/material';
import { Functions as FunctionsIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { formatNumber } from '../../utils/formatters';
import { getNarrationChipProps } from '../../utils/narration';

const CALC_TYPE_LABELS = {
  simple_interest: 'Simple Interest',
  compound_interest: 'Compound Interest',
  compound_linear: 'Compound Linear',
};

// Neutral gray group-boundary divider: marks where the Current group and the
// Proposed group each begin (on the Rate cells). Monochrome so it doesn't read
// as a status colour or fight the impact-row highlight / signed-amount colours.
const groupStartSx = { borderLeft: '2px solid rgba(0, 0, 0, 0.18)' };

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const str = String(dateStr).substring(0, 10);
    return format(new Date(str + 'T12:00:00'), 'dd MMM yyyy');
  } catch {
    return '-';
  }
};

// Signed-amount formatter: outflows red & parenthesised, inflows blue.
const formatFlowAmount = (value, flowType) => {
  if (value == null) return '-';
  const num = parseFloat(value);
  if (num === 0) return '-';
  const formatted = formatNumber(num, 2);
  if (flowType === 'outflow') return <span style={{ color: '#d32f2f' }}>({formatted})</span>;
  if (flowType === 'inflow') return <span style={{ color: '#1976d2' }}>{formatted}</span>;
  return formatted;
};

// Impacted-row palette. Direction matches the Difference card: RED = proposed
// interest went UP, GREEN = went DOWN. Each direction has a light/dark pair so
// consecutive impacted rows alternate (readability), mirroring the white/gray
// striping of unchanged rows.
const IMPACT_SHADES = {
  up:   ['#ffebee', '#ffcdd2'],   // red light / dark
  down: ['#e8f5e9', '#c8e6c9'],   // green light / dark
};
const UNCHANGED_SHADES = ['#ffffff', '#fafafa'];

// Per-row direction: prefer the row's own current→proposed move; fall back to
// the table-level isIncrease when a side is missing.
const rowGoesUp = (row, isIncrease) => {
  const cur = row.current_interest;
  const prop = row.proposed_interest;
  if (cur != null && prop != null) return parseFloat(prop) > parseFloat(cur);
  return isIncrease;
};

// Outstanding/running balance: a plain positive amount (not a directional flow),
// so no parentheses or colour — just the formatted number.
const formatBalance = (value) => {
  if (value == null) return '-';
  const num = parseFloat(value);
  if (Number.isNaN(num)) return '-';
  return formatNumber(num, 2);
};

const getRowBackground = (row, idx, isIncrease) => {
  const shade = idx % 2; // 0 = light, 1 = dark
  if (!row.is_impacted) {
    return { backgroundColor: UNCHANGED_SHADES[shade] };
  }
  const pair = rowGoesUp(row, isIncrease) ? IMPACT_SHADES.up : IMPACT_SHADES.down;
  return { backgroundColor: pair[shade] };
};

const ScheduleComparisonTable = ({
  rows = [],
  isIncrease = true,
  dp = 3,
  currentLabel = 'Current',
  proposedLabel = 'Proposed',
  // Label for the kind of change this preview represents (e.g. 'Rate Change',
  // 'Capital Withdrawal'). Shown as a chip on impacted rows; a row may override
  // it via row.change_type (e.g. a one-sided structural row).
  changeTypeLabel = null,
  // Schedule version context: current = the pre-change version; next = the
  // version a submission would create (shown as "V{next}*" on changed rows,
  // with a tooltip, since the proposal isn't a finalised version yet).
  currentVersion = null,
  nextVersion = null,
  maxHeight = 500,
}) => {
  const [calcAnchorEl, setCalcAnchorEl] = useState(null);
  const [calcDetail, setCalcDetail] = useState(null);

  const handleCalcClick = (event, detail) => {
    setCalcAnchorEl(event.currentTarget);
    setCalcDetail(detail);
  };
  const handleCalcClose = () => {
    setCalcAnchorEl(null);
    setCalcDetail(null);
  };

  const headSx = { backgroundColor: '#f5f5f5', fontWeight: 'bold', fontSize: '0.75rem' };

  return (
    <>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>Date</TableCell>
              <TableCell sx={headSx}>Description</TableCell>
              <TableCell sx={headSx}>Change</TableCell>
              {currentVersion != null && <TableCell sx={headSx}>Version</TableCell>}
              <TableCell sx={{ ...headSx, ...groupStartSx }} align="right">{currentLabel} Rate</TableCell>
              <TableCell sx={headSx} align="right">{proposedLabel} Rate</TableCell>
              <TableCell sx={headSx} align="right">Days</TableCell>
              <TableCell sx={{ ...headSx, ...groupStartSx }} align="right">{currentLabel} Interest</TableCell>
              <TableCell sx={headSx} align="right">{proposedLabel} Interest</TableCell>
              <TableCell sx={{ ...headSx, ...groupStartSx }} align="right">{currentLabel} Capital</TableCell>
              <TableCell sx={headSx} align="right">{proposedLabel} Capital</TableCell>
              <TableCell sx={{ ...headSx, ...groupStartSx }} align="right">{currentLabel} Total</TableCell>
              <TableCell sx={headSx} align="right">{proposedLabel} Total</TableCell>
              <TableCell sx={{ ...headSx, ...groupStartSx }} align="right">{currentLabel} Balance</TableCell>
              <TableCell sx={headSx} align="right">{proposedLabel} Balance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, idx) => (
              <TableRow
                key={idx}
                sx={{
                  '& .MuiTableCell-root': { fontSize: '0.75rem' },
                  ...getRowBackground(row, idx, isIncrease),
                }}
              >
                <TableCell>{formatDate(row.payment_date)}</TableCell>
                <TableCell>
                  <Chip
                    label={row.narration}
                    size="small"
                    {...getNarrationChipProps(row.narration)}
                    // Opaque fill so the chip stands out against the tinted impact row
                    // rather than letting the row colour bleed through.
                    sx={{ height: 18, fontSize: '0.7rem', ...(getNarrationChipProps(row.narration).sx || {}), backgroundColor: '#fff' }}
                  />
                </TableCell>
                <TableCell>
                  {row.is_impacted && (row.change_type || changeTypeLabel) && (
                    <Chip
                      label={row.change_type || changeTypeLabel}
                      size="small"
                      variant="outlined"
                      color={rowGoesUp(row, isIncrease) ? 'error' : 'success'}
                      sx={{ height: 18, fontSize: '0.7rem', backgroundColor: '#fff' }}
                    />
                  )}
                </TableCell>
                {currentVersion != null && (
                  <TableCell>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      <span>{`V${currentVersion}`}</span>
                      {row.is_impacted && nextVersion != null && (
                        <Tooltip title="Draft — the new schedule version that will be created when this change is approved.">
                          <span style={{ color: '#1976d2', cursor: 'help' }}>{`→ V${nextVersion}*`}</span>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                )}
                <TableCell align="right" sx={groupStartSx}>
                  {row.current_rate != null ? `${parseFloat(row.current_rate).toFixed(2)}%` : '-'}
                </TableCell>
                <TableCell align="right">
                  {row.proposed_rate != null ? `${parseFloat(row.proposed_rate).toFixed(2)}%` : '-'}
                </TableCell>
                <TableCell align="right">
                  {String(row.narration || '').startsWith('Interest Accrual') ? (row.days_in_period || '-') : '-'}
                </TableCell>
                <TableCell align="right" sx={groupStartSx}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                    {formatFlowAmount(row.current_interest, row.flow_type)}
                    {row.current_calc_detail && (
                      <IconButton
                        size="small"
                        onClick={(e) => handleCalcClick(e, row.current_calc_detail)}
                        sx={{ p: 0.25 }}
                        title="View calculation detail"
                      >
                        <FunctionsIcon sx={{ fontSize: 14 }} color="primary" />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                    {formatFlowAmount(row.proposed_interest, row.flow_type)}
                    {row.calc_detail && (
                      <IconButton
                        size="small"
                        onClick={(e) => handleCalcClick(e, row.calc_detail)}
                        sx={{ p: 0.25 }}
                        title="View calculation detail"
                      >
                        <FunctionsIcon sx={{ fontSize: 14 }} color="primary" />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={groupStartSx}>{formatFlowAmount(row.current_capital, row.flow_type)}</TableCell>
                <TableCell align="right">{formatFlowAmount(row.proposed_capital, row.flow_type)}</TableCell>
                <TableCell align="right" sx={groupStartSx}>{formatFlowAmount(row.current_total, row.flow_type)}</TableCell>
                <TableCell align="right">{formatFlowAmount(row.proposed_total, row.flow_type)}</TableCell>
                <TableCell align="right" sx={groupStartSx}>{formatBalance(row.current_balance)}</TableCell>
                <TableCell align="right">{formatBalance(row.proposed_balance)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Calculation Detail popover — real-method formula for the clicked value */}
      <Popover
        open={Boolean(calcAnchorEl)}
        anchorEl={calcAnchorEl}
        onClose={handleCalcClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ sx: { p: 2, maxWidth: 480, minWidth: 320 } }}
      >
        {calcDetail && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <FunctionsIcon color="primary" fontSize="small" />
              <Typography variant="subtitle2" fontWeight="bold">Calculation Detail</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip
                label={CALC_TYPE_LABELS[calcDetail.calc_type] || 'Simple Interest'}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {calcDetail.mode === 'current'
                ? "Matches the booked schedule using the contract's calculation method."
                : "Projected using the contract's calculation method."}
            </Typography>

            {calcDetail.formula_display && (
              <Box sx={{
                backgroundColor: '#f5f5f5',
                borderRadius: 1,
                p: 1.5,
                mb: 1,
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                wordBreak: 'break-all',
              }}>
                {calcDetail.formula_display}
              </Box>
            )}

            {calcDetail.input_params && (
              <Table size="small" sx={{ mb: 1 }}>
                <TableBody>
                  {Object.entries(calcDetail.input_params).map(([key, value]) => (
                    <TableRow key={key} sx={{ '& td': { py: 0.25, border: 'none' } }}>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem', pl: 0, width: '45%' }}>
                        {key.replace(/_/g, ' ')}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace', pr: 0 }}>
                        {typeof value === 'number'
                          ? (key.includes('rate') || key.includes('frequency') || key.includes('basis') || key.includes('days')
                            ? value
                            : `R ${formatNumber(value, dp)}`)
                          : String(value)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" color="textSecondary">
              Calculated: R {formatNumber(calcDetail.calculated_value, dp)}
            </Typography>
          </Box>
        )}
      </Popover>
    </>
  );
};

export default ScheduleComparisonTable;
